import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import ChatConversation from './ChatConversation';
import ChatSidebar from './ChatSidebar';

import { useFetchTranscriptList } from '@hooks/getQueries';
import { CircularProgress, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import TranscriptSelectionDialog from './TranscriptSelectionDialog';

function ChatWithDataMain({ id, newChatId, paramChatId, isGettingPreviousChat }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isDeepSearchMode, setIsDeepSearchMode] = useState(false);
  const [isFullScreen] = useState(false);
  const [resetCount, setResetCount] = useState(0);
  const [activeChatId, setActiveChatId] = useState('');
  const [openTranscriptSelectionModal, setOpenTranscriptSelectionModal] = useState(false);
  const [filteredTagList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [finalSelectedTranscripts, setFinalSelectedTranscripts] = useState([]);
  const [allTranscriptIds, setAllTranscriptIds] = useState([]);
  const [isTranscriptListUpdated, setIsTranscriptListUpdated] = useState(false);
  const [currentChatId, setCurrentChatId] = useState('');
  const [initialSelectedTranscripts, setInitialSelectedTranscripts] = useState([]);
  const { data: transcripts } = useFetchTranscriptList(id, filteredTagList);

  const transcriptall = transcripts?.pages?.[0];

  useEffect(() => {
    const all = transcripts?.pages?.flatMap((p) => p?.transcripts ?? []) ?? [];
    setAllTranscriptIds(all.map((t) => t.id));
  }, [transcripts]);

  // Set activeChatId when we have a specific chat ID from URL
  useEffect(() => {
    if (newChatId && newChatId !== 'new-conversation' && newChatId !== '-') {
      setActiveChatId(newChatId);
    } else if (newChatId === 'new-conversation') {
      setActiveChatId(''); // Reset for new conversations
    }
  }, [newChatId]);

  useEffect(() => {
    if (openTranscriptSelectionModal) {
      setInitialSelectedTranscripts(selected);
      setIsTranscriptListUpdated(false);
    }
  }, [openTranscriptSelectionModal]);

  useEffect(() => {
    if (!openTranscriptSelectionModal) return;
    const a = new Set(initialSelectedTranscripts);
    const b = new Set(selected);
    const sameSize = a.size === b.size;
    const allMatch = initialSelectedTranscripts.every((id) => b.has(id));

    setIsTranscriptListUpdated(!(sameSize && allMatch));
  }, [selected, initialSelectedTranscripts, openTranscriptSelectionModal]);

  const handleNewChatClick = () => {
    setActiveChatId('');
    setResetCount((prev) => prev + 1);
    setIsDeepSearchMode(false);
    setFinalSelectedTranscripts([]);
    setSelected([]);
    queryClient.invalidateQueries({ queryKey: ['chat-history', id] });
    navigate(`/interview/${id}/chat-with-data/new-conversation`);
  };
  const handleCloseTranscriptSelectionModal = () => {
    setOpenTranscriptSelectionModal(false);
  };
  const removeSelectedTranscript = (id) => {
    const newSelected = selected.filter((x) => x !== id);
    setSelected(newSelected);
    setFinalSelectedTranscripts(newSelected);
    setIsTranscriptListUpdated(true);
  };

  const handleStartFromDialog = (selectedIds) => {
    setFinalSelectedTranscripts(selectedIds);
    setSelected(selectedIds);
    setOpenTranscriptSelectionModal(false);
  };

  const getTranscriptName = (transcriptId) => {
    const allTranscripts = transcripts?.pages?.flatMap((page) => page.transcripts) || [];
    const foundItem = allTranscripts.find((t) => t.id === transcriptId);
    if (foundItem?.name) return foundItem.name;
    const idx = allTranscripts.findIndex((t) => t.id === transcriptId);
    return `Transcript ${idx >= 0 ? idx + 1 : transcriptId}`;
  };

  return (
    <Box
      sx={{
        position: isFullScreen ? 'fixed' : 'relative',
        inset: isFullScreen ? 0 : 'auto',
        zIndex: isFullScreen ? 1300 : 'auto',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
      }}
    >
      <ChatSidebar
        id={id}
        setActiveChatId={setActiveChatId}
        activeChatId={activeChatId ?? paramChatId}
        handleNewChatClick={handleNewChatClick}
        setIsTranscriptListUpdated={setIsTranscriptListUpdated}
        isFullScreen={isFullScreen}
        setCurrentChatId={setCurrentChatId}
      />
      {isGettingPreviousChat ? (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            ml: '280px',
            position: 'relative',
            height: 'calc(100vh - 140px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            py={4}
          >
            <CircularProgress color="primary" size={48} />
            <Typography variant="h6" mt={2} color="text.secondary">
              Loading Previous Conversation...
            </Typography>
          </Box>
        </Box>
      ) : (
        <ChatConversation
          id={id}
          chatId={activeChatId}
          newChatId={newChatId}
          resetCount={resetCount}
          isDeepSearchMode={isDeepSearchMode}
          setIsDeepSearchMode={setIsDeepSearchMode}
          finalSelectedTranscripts={finalSelectedTranscripts}
          setFinalSelectedTranscripts={setFinalSelectedTranscripts}
          setSelected={setSelected}
          allTranscriptIds={allTranscriptIds}
          getTranscriptName={getTranscriptName}
          transcripts={transcriptall}
          openTranscriptSelectionModal={openTranscriptSelectionModal}
          setOpenTranscriptSelectionModal={setOpenTranscriptSelectionModal}
          isTranscriptListUpdated={isTranscriptListUpdated}
          setIsTranscriptListUpdated={setIsTranscriptListUpdated}
          toggle={removeSelectedTranscript}
          onChatIdChange={setActiveChatId}
          currentChatId={currentChatId}
          setCurrentChatId={setCurrentChatId}
          // isGettingPreviousChat={isGettingPreviousChat}
        />
      )}

      <TranscriptSelectionDialog
        open={openTranscriptSelectionModal}
        onClose={handleCloseTranscriptSelectionModal}
        onStart={handleStartFromDialog}
        id={id}
      />
    </Box>
  );
}

export default ChatWithDataMain;
